import { useMemo } from "react";
import { EntityCombobox } from "@/components/ui/entity-combobox";
import { useProjects, type ProjectWithRelations } from "@/hooks/useProjects";
import { formatProjectName } from "@/lib/format-project-name";

interface CopyFromProjectSelectorProps {
  value: string | null;
  onChange: (projectId: string | null) => void;
  currentProjectId?: string;
  placeholder?: string;
  disabled?: boolean;
  allowClear?: boolean;
  testId?: string;
}

function projectLabel(project: ProjectWithRelations) {
  const name = formatProjectName(project.name, project.event_start_date);
  return project.number ? `${project.number} - ${name}` : name;
}

function projectSearchLabel(project: ProjectWithRelations) {
  return [
    project.number,
    formatProjectName(project.name, project.event_start_date),
  ]
    .filter(Boolean)
    .join(" ");
}

export function CopyFromProjectSelector({
  value,
  onChange,
  currentProjectId,
  placeholder = "Select project",
  disabled,
  allowClear = true,
  testId,
}: CopyFromProjectSelectorProps) {
  const { data: projects = [] } = useProjects();
  const options = useMemo(
    () =>
      projects
        .filter(
          (project) => !project.deleted_at && project.id !== currentProjectId,
        )
        .sort((a, b) => projectLabel(a).localeCompare(projectLabel(b)))
        .map((project) => ({
          value: project.id,
          label: projectSearchLabel(project),
          subtitle: project.companies?.name ?? undefined,
        })),
    [currentProjectId, projects],
  );

  return (
    <EntityCombobox
      options={options}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      searchPlaceholder="Search projects..."
      emptyMessage="No projects found."
      disabled={disabled}
      allowClear={allowClear}
      testId={testId}
    />
  );
}
